import React, {useMemo} from 'react';
import {IconText, ProjectIndicator, Table} from 'web-components';
import {LoadMore} from '../../../loaders/LoadMore';
import MonthByMonthProjectLoader, {monthByMonthProjectLoaderQuery} from './MonthByMonthProjectLoader';
import MonthByMonthTotalsLoader, {monthByMonthTotalsLoaderQuery} from './MonthByMonthTotalsLoader';
import {useIntl} from 'react-intl';
import {filtersToSearchQuery, getPhaseName, getRoleName} from '../ProjectPortfolioReportUtils';
import ProjectPortfolioMonthByMonthRow from './ProjectPortfolioMonthByMonthRow';
import MonthByMonthPhaseLoader, {monthByMonthPhaseLoaderQuery} from './MonthByMonthPhaseLoader';
import MonthByMonthRoleLoader, {monthByMonthRoleLoaderQuery} from './MonthByMonthRoleLoader';
import ProjectPortfolioMonthByMonthDownload, {
	projectPortfolioMonthByMonthDownloadQuery,
} from './ProjectPortfolioMonthByMonthDownload';
import {PHASE_BREAKDOWN_TYPE} from '../../../../constants';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import ForecastTooltip from '../../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import {ProjectPortfolioReportTooltips} from '../ProjectPortfolioReportTooltips';

export const ProjectPortfolioMonthByMonthTable = ({
	filters,
	selectedDateRange: {startDate, endDate},
	selectedSingleValue,
	setCsvDownloadFunction,
	hasBaseline,
	theEyeOptionsNestedTree,
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const pageSize = 100;
	const year = startDate.year();

	const boolEyeOptions = useMemo(() => {
		const bools = {};
		theEyeOptionsNestedTree.forEach(eyeOption => {
			bools[eyeOption.name] = eyeOption.checked;
			if (eyeOption.nestedOptions) {
				eyeOption.nestedOptions.forEach(nestedOption => {
					bools[nestedOption.name] = nestedOption.checked;
					if (nestedOption.nestedOptions) {
						nestedOption.nestedOptions.forEach(subNestedOption => {
							bools[subNestedOption.name] = subNestedOption.checked;
						});
					}
				});
			}
		});
		return bools;
	}, [theEyeOptionsNestedTree]);

	return (
		<Table paddedCells>
			<Table.Header>
				<Table.HeaderColumn visible flex={1} width="EXTRA_LARGE" />

				{/* Project Overview */}
				<Table.HeaderGrouping name={formatMessage({id: 'project_budget.project_overview'})}>
					<Table.HeaderColumn visible={boolEyeOptions.mbm_winProbability} usePadding align="right" width="MEDIUM">
						<ForecastTooltip content={ProjectPortfolioReportTooltips.WinProbabilityTooltip(formatMessage)}>
							<Table.HeaderColumn.Title>
								{formatMessage({id: 'project_portfolio_report.win_prob'})}
							</Table.HeaderColumn.Title>
						</ForecastTooltip>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.mbm_dates} usePadding align="left" width="SMALL">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.start'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.mbm_dates} usePadding align="left" width="SMALL">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.end'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.mbm_client} usePadding align="left" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'insights.component.list.column.client'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.mbm_status} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.status'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.mbm_projectOwner} usePadding align="left" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.project_owner'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.mbm_stage} usePadding align="left" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'insights.component.list.column.status'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>

					<Table.HeaderColumn visible={boolEyeOptions.mbm_budgetType} usePadding align="left" width="MEDIUM">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'new_project_modal.budget_type'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.mbm_rateCard} usePadding align="left" width="LARGE">
						<Table.HeaderColumn.Title>
							{formatMessage({id: 'insights.component.list.column.rateCard'})}
						</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
					<Table.HeaderColumn visible={boolEyeOptions.mbm_progress} usePadding align="right" width="MEDIUM">
						<Table.HeaderColumn.Title>{formatMessage({id: 'common.progress'})}</Table.HeaderColumn.Title>
					</Table.HeaderColumn>
				</Table.HeaderGrouping>

				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.jan',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.feb',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.mar',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.apr',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.may',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.jun',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.jul',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.aug',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.sep',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.oct',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.nov',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
				<Table.HeaderColumn usePadding align="right" visible={true} width="MEDIUM_SMALL">
					<Table.HeaderColumn.Title>{`${formatMessage({
						id: 'insights.component.list.column.dec',
					})} ${year}`}</Table.HeaderColumn.Title>
				</Table.HeaderColumn>
			</Table.Header>
			<LoadMore
				key="query-render-month-by-month-totals"
				query={monthByMonthTotalsLoaderQuery}
				variables={{
					searchQuery: filtersToSearchQuery(filters),
					startYear: startDate.year(),
					startMonth: startDate.month() + 1,
					startDay: startDate.date(),
					endYear: endDate.year(),
					endMonth: endDate.month() + 1,
					endDay: endDate.date(),
					selectedSingleValue: selectedSingleValue,
				}}
				componentName="project_portfolio_month_by_month_totals_loader"
				loader={<MonthByMonthTotalsLoader />}
			>
				{({data}) => {
					return (
						<Table.Rows data={data} canExpand={false} hasHeader>
							{({rowData, tableColumnsProps}) => {
								return (
									<ProjectPortfolioMonthByMonthRow
										rowData={rowData}
										tableColumnProps={tableColumnsProps}
										headerColumn={<Table.Column>{formatMessage({id: 'common.totals'})}</Table.Column>}
									/>
								);
							}}
						</Table.Rows>
					);
				}}
			</LoadMore>
			<LoadMore
				key="query-render-month-by-month-project"
				query={monthByMonthProjectLoaderQuery}
				variables={{
					searchQuery: filtersToSearchQuery(filters),
					startYear: startDate.year(),
					startMonth: startDate.month() + 1,
					startDay: startDate.date(),
					endYear: endDate.year(),
					endMonth: endDate.month() + 1,
					endDay: endDate.date(),
					pageSize: pageSize,
					selectedSingleValue: selectedSingleValue,
				}}
				componentName="project_portfolio_month_by_month_project_loader"
				loader={<MonthByMonthProjectLoader />}
			>
				{({data, loadMore}) => {
					setCsvDownloadFunction(() => {
						showModal({
							type: MODAL_TYPE.DOWNLOADING_CSV_DATA,
							componentName: 'project_portfolio_month_by_month_project_csv_loader',
							query: projectPortfolioMonthByMonthDownloadQuery,
							variables: {
								searchQuery: filtersToSearchQuery(filters),
								startYear: startDate.year(),
								startMonth: startDate.month() + 1,
								startDay: startDate.date(),
								endYear: endDate.year(),
								endMonth: endDate.month() + 1,
								endDay: endDate.date(),
								pageSize: 1000000,
								selectedSingleValue: selectedSingleValue,
							},
							createCsvDownload: <ProjectPortfolioMonthByMonthDownload />,
							startDate,
							endDate,
						});
					});
					return (
						<Table.Rows data={data} canExpand={true} loadMore={loadMore} autoload={true}>
							{({rowData, tableColumnsProps, expanded, nextLevelProps: phaseLevelProps}) => {
								const fullAccessToProject = rowData?.project?.fullAccessToProject;
								const fixedPriceLocks = rowData?.project?.fixedPriceLocks;
								return (
									<>
										<ProjectPortfolioMonthByMonthRow
											rowData={rowData}
											tableColumnProps={tableColumnsProps}
											headerColumn={
												<IconText
													key={rowData.project.id}
													text={
														fullAccessToProject
															? rowData.project.name
															: intl.formatMessage({id: 'common.no_project_access'})
													}
												>
													<ProjectIndicator
														project={rowData.project}
														name={rowData.project.name}
														id={rowData.project.companyProjectId}
														color={rowData.project.projectColor}
														noTitle={!fullAccessToProject}
													/>
												</IconText>
											}
											selectedSingleValue={selectedSingleValue}
											hasBaseline={hasBaseline}
											fixedPriceLocks={fixedPriceLocks}
										/>
										{expanded && (
											<LoadMore
												key="query-render-month-by-month-phase"
												query={monthByMonthPhaseLoaderQuery}
												componentName="project_portfolio_month_by_month_phase_loader"
												loader={<MonthByMonthPhaseLoader />}
												variables={{
													projectId: rowData.project.id,
													startYear: startDate.year(),
													startMonth: startDate.month() + 1,
													startDay: startDate.date(),
													endYear: endDate.year(),
													endMonth: endDate.month() + 1,
													endDay: endDate.date(),
													selectedSingleValue: selectedSingleValue,
												}}
											>
												{({data: phaseBreakdownData}) => {
													return (
														<Table.Rows
															data={phaseBreakdownData}
															{...phaseLevelProps}
															canExpand={true}
														>
															{({
																rowData: phaseRowData,
																tableColumnsProps: phaseTableColumnProps,
																expanded: phaseExpanded,
																nextLevelProps: roleLevelProps,
															}) => {
																return (
																	<>
																		<ProjectPortfolioMonthByMonthRow
																			rowData={phaseRowData}
																			tableColumnProps={phaseTableColumnProps}
																			headerColumn={getPhaseName(intl, phaseRowData)}
																			canExpand={
																				phaseRowData.breakdownType ===
																					PHASE_BREAKDOWN_TYPE.PHASE ||
																				phaseRowData.breakdownType ===
																					PHASE_BREAKDOWN_TYPE.NO_PHASE
																			}
																			selectedSingleValue={selectedSingleValue}
																			fixedPriceLocks={fixedPriceLocks}
																		/>
																		{phaseExpanded && (
																			<LoadMore
																				key="query-render-month-by-month-role"
																				query={monthByMonthRoleLoaderQuery}
																				componentName="project_portfolio_month_by_month_role_loader"
																				loader={<MonthByMonthRoleLoader />}
																				variables={{
																					projectId: rowData.project.id,
																					phaseId:
																						phaseRowData.breakdownType ===
																						PHASE_BREAKDOWN_TYPE.PHASE
																							? phaseRowData.phase.id
																							: phaseRowData.breakdownType ===
																							  PHASE_BREAKDOWN_TYPE.NO_PHASE
																							? 0
																							: null,
																					startYear: startDate.year(),
																					startMonth: startDate.month() + 1,
																					startDay: startDate.date(),
																					endYear: endDate.year(),
																					endMonth: endDate.month() + 1,
																					endDay: endDate.date(),
																					selectedSingleValue: selectedSingleValue,
																				}}
																			>
																				{({data: roleBreakdownData}) => {
																					return (
																						<Table.Rows
																							data={roleBreakdownData}
																							{...roleLevelProps}
																							canExpand={false}
																						>
																							{({
																								rowData: roleRowData,
																								tableColumnsProps:
																									roleTableColumnProps,
																							}) => {
																								return (
																									<ProjectPortfolioMonthByMonthRow
																										rowData={roleRowData}
																										tableColumnProps={
																											roleTableColumnProps
																										}
																										headerColumn={getRoleName(
																											intl,
																											roleRowData
																										)}
																										selectedSingleValue={
																											selectedSingleValue
																										}
																										fixedPriceLocks={
																											fixedPriceLocks
																										}
																									/>
																								);
																							}}
																						</Table.Rows>
																					);
																				}}
																			</LoadMore>
																		)}
																	</>
																);
															}}
														</Table.Rows>
													);
												}}
											</LoadMore>
										)}
									</>
								);
							}}
						</Table.Rows>
					);
				}}
			</LoadMore>
		</Table>
	);
};
